import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar, Eye, ArrowRight, Megaphone, Percent } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import OptimizedImage from '../components/OptimizedImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CampaignArchive = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/pages?page_type=campaign&published_only=true`);
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Kampanjer | Sand-tec AS</title>
        <meta name="description" content="Se våre aktive kampanjer og tilbud på EU-kontroll, service og verkstedtjenester" />
      </Helmet>

      {/* Noscript fallback for AI agents, Bingbot and crawlers without JS */}
      <noscript>
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '30px 20px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff', color: '#1a1a1a'}}>
          <header style={{borderBottom: '3px solid #9333ea', paddingBottom: '20px', marginBottom: '30px'}}>
            <h1 style={{fontSize: '2em', color: '#9333ea', margin: '0 0 8px'}}>Kampanjer og tilbud — Sand-tec AS</h1>
            <p style={{fontSize: '1.1em', color: '#555', margin: 0}}>Se våre aktive kampanjer og spesialtilbud på EU-kontroll, service og verkstedtjenester for tungbil, tilhenger og personbil.</p>
          </header>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.5em', color: '#1f2937', marginBottom: '10px'}}>Aktive kampanjer</h2>
            <p style={{lineHeight: '1.7', color: '#374151'}}>Sand-tec AS tilbyr regelmessig kampanjer og rabatter på verkstedtjenester. Vi har tilbud på EU-kontroll, dekkskift, flushing av automatgirkasse og andre vedlikeholdstjenester. Besøk denne siden jevnlig for oppdaterte kampanjer.</p>
            <div style={{padding: '15px', backgroundColor: '#f3e8ff', borderLeft: '4px solid #9333ea', marginTop: '15px'}}>
              <h3 style={{margin: '0 0 5px', color: '#7e22ce'}}>Flushing av automatgirkasse</h3>
              <p style={{margin: 0, color: '#374151'}}>Regelmessig flushing av automatgirkasse forebygger kostbare reparasjoner og sikrer optimal girfunksjon. Ved vårt verksted på Sagstua i Nord-Odal utfører vi komplett oljeskift via maskinell flushing.</p>
              <a href="/kampanje/flushing-av-automatgirkasse" style={{color: '#9333ea', fontWeight: 'bold'}}>Les mer om kampanjen</a>
            </div>
          </section>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Våre tjenester til kampanjepris</h2>
            <ul style={{lineHeight: '2', color: '#374151', paddingLeft: '20px'}}>
              <li>EU-kontroll for tungbil, tilhenger og personbil</li>
              <li>Fartsskriver 2-års og 6-årskontroll</li>
              <li>Dekkskift og dekkhotell for lastebil</li>
              <li>Flushing av automatgirkasse</li>
              <li>Service og vedlikehold</li>
            </ul>
          </section>

          <nav style={{marginBottom: '30px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
            <h3 style={{fontSize: '1.1em', color: '#374151', marginBottom: '10px'}}>Navigasjon</h3>
            <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
              <li><a href="/" style={{color: '#9333ea'}}>Forside</a></li>
              <li><a href="/tjenester" style={{color: '#9333ea'}}>Tjenester</a></li>
              <li><a href="/aktuelt" style={{color: '#9333ea'}}>Aktuelt</a></li>
              <li><a href="/referanser" style={{color: '#9333ea'}}>Referanser</a></li>
              <li><a href="/anmeldelser" style={{color: '#9333ea'}}>Anmeldelser</a></li>
              <li><a href="/kontakt" style={{color: '#9333ea'}}>Kontakt</a></li>
            </ul>
          </nav>

          <footer style={{borderTop: '1px solid #e5e7eb', paddingTop: '15px', fontSize: '0.9em', color: '#6b7280'}}>
            <p>Kontakt oss for mer informasjon.</p>
            <p style={{marginTop: '5px'}}>For maskinlesbar bedriftsinformasjon, se <a href="/api/llms.txt" style={{color: '#9333ea'}}>llms.txt</a> | <a href="/api/llms-full.txt" style={{color: '#9333ea'}}>llms-full.txt</a></p>
          </footer>
        </div>
      </noscript>

      <div className="min-h-screen bg-black">
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-purple-900/20 pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.1),transparent_50%)]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/50 mb-6 px-4 py-2">
                <Percent className="w-4 h-4 inline mr-2" />
                Kampanjer & Tilbud
              </Badge>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent">
                  Aktive Kampanjer
                </span>
              </h1>
              <p className="text-xl text-gray-300">
                Få rabatt og spesialtilbud på våre tjenester
              </p>
            </div>
          </div>
        </div>

        {/* Campaigns Grid */}
        <div className="container mx-auto px-4 py-20">
          {loading ? (
            <div className="text-center text-gray-400">Laster kampanjer...</div>
          ) : pages.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <Megaphone className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-xl">Ingen aktive kampanjer for øyeblikket</p>
              <p className="text-sm mt-2">Kom tilbake snart for nye tilbud!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {pages.map((page) => (
                <Card
                  key={page.id}
                  onClick={() => navigate(`/kampanje/${page.slug}`)}
                  className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border-purple-700/50 hover:border-purple-600 cursor-pointer transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/30 group relative overflow-hidden"
                >
                  {/* Special Offer Badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-lg">
                      <Percent className="w-3 h-3 mr-1" />
                      TILBUD
                    </Badge>
                  </div>

                  {page.featured_image && (
                    <div className="w-full h-48 overflow-hidden rounded-t-lg">
                      <OptimizedImage
                        src={page.featured_image}
                        alt={page.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center space-x-4 text-sm text-gray-400 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(page.created_at).toLocaleDateString('no-NO', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}</span>
                      </div>
                      {page.views > 0 && (
                        <div className="flex items-center space-x-1">
                          <Eye className="w-4 h-4" />
                          <span>{page.views}</span>
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-white group-hover:text-purple-300 transition-colors line-clamp-2">
                      {page.title}
                    </CardTitle>
                    {page.excerpt && (
                      <CardDescription className="text-gray-400 line-clamp-3 mt-2">
                        {page.excerpt}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-purple-400 group-hover:text-purple-300 font-semibold">
                      <span>Se detaljer</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CampaignArchive;
